import type { LocationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Location/location.interface";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { localeState, updateLocale } from "@verbleif/lib/src/translations/localeState";
import { resetMyAppStoreAccess } from "@verbleif/shared/src/composables/AppStore/useMyAppStoreAccess";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { ensureLoginCatalogList } from "@verbleif/shared/src/composables/Query/useResourceCatalog";
import { useSharedStore } from "@verbleif/shared/src/composables/SharedStore/useSharedStore";
import { useSystemStore } from "@verbleif/shared/src/composables/SystemStore/useSystemStore";
import { loadSession } from "@web/core/onAuthenticateSuccess";
import { clearPersistedQueryCache } from "@web/core/queryPersister";
import moment from "moment";

/**
 * Reloads the session after the effective user changed without a new login (impersonation start
 * or stop). The caller must have changed the `X-Switch-User` identifier first, so every request
 * below already goes out as the new identity. Gleap and Sentry keep the real (staff) identity.
 */
export async function reloadSessionAfterIdentityChange(): Promise<void> {
  const locationStore = useLocationStore();

  // Drop what the previous identity could see. `resetQueries` (not `clear`) keeps the catalog
  // observers alive and refetches them with the new identity.
  clearPersistedQueryCache();
  await getQueryClient().resetQueries();
  useSharedStore().reset();
  useSystemStore().reset();
  // The App Store share access is kept per organisation outside the query cache.
  resetMyAppStoreAccess();

  // The new identity may not have access to the location the previous one had selected.
  const selected = locationStore.selectedLocation.value;
  if (selected) {
    const locations = await ensureLoginCatalogList<LocationHydraItem>("locations");
    if (!locations.some(location => location["@id"] === selected)) {
      locationStore.setSelectedLocation(null);
    }
  }

  const user = await loadSession();

  // App.vue only follows `user.locale` outside skipAuthChecks routes, and the impersonate route is one.
  updateLocale(user.locale?.toLowerCase() ?? "nl");
  moment.locale(localeState.value);
}
